import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_MS = 30_000;

const defaultValue = {
  items: [],
  unread: 0,
  assignmentVersion: 0,
  refresh: async () => {},
  markAllRead: async () => {},
  markRead: async () => {},
};

const NotificationsContext = createContext(defaultValue);

export function NotificationsProvider({ children }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [assignmentVersion, setAssignmentVersion] = useState(0);
  const seenAssignedIds = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/clinical/notifications`);
      const next = res.data?.items || [];
      setItems(next);
      setUnread(res.data?.unread_count || 0);

      const currentAssignedIds = new Set(
        next.filter((n) => n.type === 'patient_assigned').map((n) => n.id),
      );
      if (seenAssignedIds.current === null) {
        seenAssignedIds.current = currentAssignedIds;
      } else {
        let hasNew = false;
        for (const id of currentAssignedIds) {
          if (!seenAssignedIds.current.has(id)) {
            hasNew = true;
            break;
          }
        }
        seenAssignedIds.current = currentAssignedIds;
        if (hasNew) setAssignmentVersion((v) => v + 1);
      }
    } catch {
      // silent — bell shouldn't crash the page
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.put(`${API_URL}/api/v1/clinical/notifications/read-all`);
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch {}
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await axios.put(`${API_URL}/api/v1/clinical/notifications/${id}/read`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {}
  }, []);

  return (
    <NotificationsContext.Provider
      value={{ items, unread, assignmentVersion, refresh: load, markAllRead, markRead }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);
